import { useState, type FormEvent } from 'react';

import { Button, Card, Dropdown, Field, Input, Skeleton, StatusPill } from '@/design-system';
import {
    MAIL_DRIVER_LABELS,
    MAIL_DRIVERS,
    MAIL_ENCRYPTION_LABELS,
    MAIL_ENCRYPTIONS,
    type MailSettings,
    type MailSettingsInput,
} from '@/entities/tenant';
import { errorMessage, isApiError, useSessionStore } from '@/shared/api';
import { formatDayTimeShort } from '@/shared/lib';
import { ErrorState } from '@/shared/ui';

import { mailCopy } from '../copy';
import { useMailSettings, useSendTestMail, useUpdateMailSettings } from '../hooks';

const SMALL = 'text-[12px] leading-[1.45] text-[var(--text-tertiary-safe)]';

export interface MailServerCardProps {
    /** Alleen de eigenaar ziet de mailserver: er staan inloggegevens in. */
    canManage: boolean;
}

/**
 * Eigen mailserver en afzender (`/tenant/mail-settings`).
 *
 * Zolang er geen geslaagd testbericht via de eigen server is, verstuurt
 * Smartkeur onder zijn eigen afzender met de naam van het bedrijf ervoor. Dat
 * staat hier permanent in beeld: een rapport dat "van Smartkeur" lijkt te komen
 * ondermijnt de white-label (patterns.md §10), en dat mag je niet per ongeluk
 * ontdekken bij de eerste klant.
 *
 * Welke velden er zijn, hangt af van de verzendmethode - precies zoals de API
 * ze gebruikt: SMTP en Lettermint een server met inlog, Amazon SES een regio met
 * sleutels, Postmark één serversleutel.
 */
export function MailServerCard({ canManage }: MailServerCardProps) {
    const mail = useMailSettings(canManage);

    return (
        <Card padding="lg" as="section" aria-labelledby="mail-title">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <h2
                    id="mail-title"
                    className="m-0 font-[family-name:var(--font-display)] text-[15px] leading-[1.3] font-semibold text-[var(--text-primary)]"
                >
                    {mailCopy.title}
                </h2>
                {mail.data ? <MailStatus mail={mail.data} /> : null}
            </div>
            <p className={`m-0 mt-1 mb-3 ${SMALL}`}>{mailCopy.intro}</p>

            {!canManage ? (
                <p className={`m-0 ${SMALL}`}>{mailCopy.ownerOnly}</p>
            ) : mail.isPending ? (
                <div aria-busy="true">
                    <Skeleton height={14} label={`${mailCopy.title} laden`} />
                    <Skeleton width="70%" height={14} className="mt-2" />
                </div>
            ) : mail.isError ? (
                <ErrorState
                    error={mail.error}
                    title={mailCopy.title}
                    onRetry={() => void mail.refetch()}
                />
            ) : (
                <MailForm mail={mail.data} />
            )}
        </Card>
    );
}

function MailStatus({ mail }: { mail: MailSettings }) {
    if (mail.driver === 'platform') {
        return (
            <StatusPill tone="neutral" size="sm">
                {mailCopy.statusPlatform}
            </StatusPill>
        );
    }

    return mail.usesPlatformFallback ? (
        <StatusPill tone="warn" size="sm">
            {mailCopy.statusUnverified}
        </StatusPill>
    ) : (
        <StatusPill tone="accent" size="sm">
            {mailCopy.statusVerified}
        </StatusPill>
    );
}

function draftOf(mail: MailSettings): MailSettingsInput {
    return {
        driver: mail.driver,
        host: mail.host,
        port: mail.port,
        username: mail.username,
        encryption: mail.encryption,
        fromName: mail.fromName,
        fromAddress: mail.fromAddress,
        replyToAddress: mail.replyToAddress,
    };
}

/** Bij het opslaan: een leeg veld haalt de waarde weg. */
function text(value: string | null): string | null {
    return value === null || value.trim() === '' ? null : value.trim();
}

function normalised(draft: MailSettingsInput): MailSettingsInput {
    return {
        ...draft,
        host: text(draft.host),
        username: text(draft.username),
        fromName: text(draft.fromName),
        fromAddress: text(draft.fromAddress),
        replyToAddress: text(draft.replyToAddress),
    };
}

function MailForm({ mail }: { mail: MailSettings }) {
    const email = useSessionStore((state) => state.session?.user.email ?? '');
    const save = useUpdateMailSettings();
    const test = useSendTestMail();
    const [draft, setDraft] = useState<MailSettingsInput>(() => draftOf(mail));
    const [password, setPassword] = useState('');
    const [recipient, setRecipient] = useState(email);
    const [savedNotice, setSavedNotice] = useState(false);

    const driver = draft.driver;
    const server = driver === 'smtp' || driver === 'lettermint';
    const fieldError = (field: string) =>
        isApiError(save.error) ? save.error.firstValidationError(field) : undefined;
    const hasFieldErrors =
        isApiError(save.error) && Object.keys(save.error.validationErrors).length > 0;

    function patch(next: Partial<MailSettingsInput>) {
        setSavedNotice(false);
        setDraft((current) => ({ ...current, ...next }));
    }

    function submit(event: FormEvent) {
        event.preventDefault();
        const input = normalised(draft);

        save.mutate(password === '' ? input : { ...input, password }, {
            onSuccess: () => {
                setPassword('');
                setSavedNotice(true);
            },
        });
    }

    const passwordLabel =
        driver === 'ses'
            ? mailCopy.secretKey
            : driver === 'postmark'
              ? mailCopy.serverKey
              : mailCopy.password;

    return (
        <>
            {mail.driver !== 'platform' && mail.usesPlatformFallback ? (
                <p
                    role="status"
                    className="m-0 mb-[14px] rounded-[7px] border border-[var(--warn-border)] bg-[var(--warn-tint)] p-[10px] text-[13px] leading-[1.5] text-[var(--warn-text)]"
                >
                    {mailCopy.fallback}
                </p>
            ) : null}

            {mail.verifiedAt && !mail.usesPlatformFallback ? (
                <p className={`m-0 mb-3 ${SMALL}`}>
                    {mailCopy.verifiedOn(formatDayTimeShort(mail.verifiedAt))}
                </p>
            ) : null}

            {mail.lastError ? (
                <p className="m-0 mb-3 text-[12px] leading-[1.45] text-[var(--warn-text)]">
                    {mailCopy.lastError(mail.lastError)}
                </p>
            ) : null}

            <form onSubmit={submit} noValidate className="grid gap-[10px] sm:grid-cols-2">
                <Field
                    label={mailCopy.driver}
                    className="sm:col-span-2"
                    error={fieldError('driver')}
                >
                    <Dropdown
                        value={driver}
                        options={MAIL_DRIVERS.map((option) => ({
                            value: option,
                            label: MAIL_DRIVER_LABELS[option],
                        }))}
                        onChange={(next) => {
                            const needsServer = next === 'smtp' || next === 'lettermint';

                            /* Wat er staat, is wat er wordt opgeslagen: TLS als er nog niets gekozen was. */
                            patch({
                                driver: next,
                                ...(needsServer && draft.encryption === null
                                    ? { encryption: 'tls' as const }
                                    : {}),
                            });
                        }}
                    />
                </Field>

                {server || driver === 'ses' ? (
                    <Field
                        label={driver === 'ses' ? mailCopy.region : mailCopy.host}
                        error={fieldError('host')}
                    >
                        <Input
                            value={draft.host ?? ''}
                            placeholder={
                                driver === 'ses'
                                    ? mailCopy.regionPlaceholder
                                    : mailCopy.hostPlaceholder
                            }
                            autoComplete="off"
                            spellCheck={false}
                            onChange={(event) => patch({ host: event.target.value })}
                        />
                    </Field>
                ) : null}

                {server ? (
                    <Field label={mailCopy.port} error={fieldError('port')}>
                        <Input
                            mono
                            inputMode="numeric"
                            value={draft.port === null ? '' : String(draft.port)}
                            onChange={(event) => {
                                const digits = event.target.value.replace(/\D/g, '');

                                patch({ port: digits === '' ? null : Number(digits) });
                            }}
                        />
                    </Field>
                ) : null}

                {server || driver === 'ses' ? (
                    <Field
                        label={driver === 'ses' ? mailCopy.accessKey : mailCopy.username}
                        error={fieldError('username')}
                    >
                        <Input
                            value={draft.username ?? ''}
                            autoComplete="off"
                            spellCheck={false}
                            onChange={(event) => patch({ username: event.target.value })}
                        />
                    </Field>
                ) : null}

                {driver === 'platform' ? null : (
                    <Field
                        label={passwordLabel}
                        description={mail.driver === driver ? mailCopy.passwordKeep : undefined}
                        error={fieldError('password')}
                    >
                        <Input
                            type="password"
                            value={password}
                            autoComplete="new-password"
                            onChange={(event) => {
                                setSavedNotice(false);
                                setPassword(event.target.value);
                            }}
                        />
                    </Field>
                )}

                {server ? (
                    <Field label={mailCopy.encryption} error={fieldError('encryption')}>
                        <Dropdown
                            value={draft.encryption ?? 'tls'}
                            options={MAIL_ENCRYPTIONS.map((option) => ({
                                value: option,
                                label: MAIL_ENCRYPTION_LABELS[option],
                            }))}
                            onChange={(encryption) => patch({ encryption })}
                        />
                    </Field>
                ) : null}

                <Field label={mailCopy.fromName} error={fieldError('from_name')}>
                    <Input
                        value={draft.fromName ?? ''}
                        onChange={(event) => patch({ fromName: event.target.value })}
                    />
                </Field>

                <Field label={mailCopy.fromAddress} error={fieldError('from_address')}>
                    <Input
                        type="email"
                        value={draft.fromAddress ?? ''}
                        autoComplete="off"
                        spellCheck={false}
                        onChange={(event) => patch({ fromAddress: event.target.value })}
                    />
                </Field>

                <Field
                    label={mailCopy.replyTo}
                    description={mailCopy.replyToHint}
                    className="sm:col-span-2"
                    error={fieldError('reply_to_address')}
                >
                    <Input
                        type="email"
                        value={draft.replyToAddress ?? ''}
                        autoComplete="off"
                        spellCheck={false}
                        onChange={(event) => patch({ replyToAddress: event.target.value })}
                    />
                </Field>

                <div className="flex flex-wrap items-center gap-3 sm:col-span-2">
                    <Button type="submit" variant="primary" size="md" disabled={save.isPending}>
                        {save.isPending ? mailCopy.saving : mailCopy.save}
                    </Button>
                    {savedNotice && !save.isPending ? (
                        <span
                            role="status"
                            className="text-[12px] leading-[1.4] text-[var(--accent-dark)]"
                        >
                            {mailCopy.saved}
                        </span>
                    ) : null}
                </div>

                {save.error && !hasFieldErrors ? (
                    <p
                        role="alert"
                        className="m-0 text-[12px] leading-[1.4] text-[var(--danger)] sm:col-span-2"
                    >
                        {errorMessage(save.error)}
                    </p>
                ) : null}
            </form>

            <div className="mt-[14px] border-t border-[var(--border-subtle)] pt-[14px]">
                <div className="flex flex-wrap items-end gap-2">
                    <Field
                        label={mailCopy.testRecipient}
                        size="sm"
                        className="min-w-[200px] flex-1"
                    >
                        <Input
                            type="email"
                            value={recipient}
                            autoComplete="off"
                            spellCheck={false}
                            onChange={(event) => setRecipient(event.target.value)}
                        />
                    </Field>
                    <Button
                        variant="outline"
                        size="sm"
                        disabled={test.isPending || recipient.trim() === ''}
                        onClick={() => test.mutate(recipient.trim())}
                    >
                        {test.isPending ? mailCopy.testing : mailCopy.test}
                    </Button>
                </div>

                {test.data ? (
                    <p
                        role="status"
                        className={[
                            'm-0 mt-2 text-[12px] leading-[1.45]',
                            test.data.delivered
                                ? 'text-[var(--accent-dark)]'
                                : 'text-[var(--warn-text)]',
                        ].join(' ')}
                    >
                        {test.data.delivered
                            ? test.data.usedPlatformFallback
                                ? mailCopy.testPlatform(test.variables ?? recipient)
                                : mailCopy.testOwnServer(test.variables ?? recipient)
                            : test.data.error
                              ? mailCopy.testFailed(test.data.error)
                              : mailCopy.testFailedUnknown}
                    </p>
                ) : null}

                {test.error ? (
                    <p
                        role="alert"
                        className="m-0 mt-2 text-[12px] leading-[1.4] text-[var(--danger)]"
                    >
                        {isApiError(test.error)
                            ? (test.error.firstValidationError('recipient') ??
                              errorMessage(test.error))
                            : errorMessage(test.error)}
                    </p>
                ) : null}
            </div>
        </>
    );
}
